import type {
  CreateMaterialCodePayload,
  MaterialCodeByDimension,
  MaterialSupplierDetail,
  MaterialCode,
} from "@/api/materialSupplierService";
import { useGetReturnScrap, useGetSupplier2nd } from "@/hooks/masterHook";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Skeleton,
  Checkbox,
} from "@mui/material";
import { useMemo } from "react";
import EditRow from "@/components/material-code/EditRow";
import ViewRow from "@/components/material-code/ViewRow";
import { useGetFirstTierSuppliers } from "@/hooks/supplierHooks";

interface MaterialCodeTableProps {
  materials: Array<MaterialCodeByDimension>;
  dimensionId: number;
  dimensionName: string;
  loading?: boolean;
  isAdding?: boolean;
  isEditing?: boolean;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (_: unknown, newPage: number) => void;
  onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  newMaterial: CreateMaterialCodePayload;
  setNewMaterial: (data: CreateMaterialCodePayload) => void;
  selectedMaterial: Array<CreateMaterialCodePayload>;
  setSelectedMaterial: (data: Array<CreateMaterialCodePayload>) => void;
  materialSupplierDetail?: MaterialSupplierDetail;
  isOpenDetail?: boolean;
  isOpenDimension?: boolean;
  isOpenComponentPrice?: boolean;
  isOpenOtherCost?: boolean;
  validationErrors?: Map<number, Set<string>>;
  tabActive: number;
}

const MaterialCodeTable = ({
  materials,
  dimensionId,
  dimensionName,
  loading,
  isAdding = false,
  isEditing = false,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  newMaterial,
  setNewMaterial,
  selectedMaterial,
  setSelectedMaterial,
  materialSupplierDetail,
  isOpenDetail = true,
  isOpenDimension = true,
  isOpenComponentPrice = true,
  isOpenOtherCost = true,
  validationErrors = new Map(),
  tabActive,
}: MaterialCodeTableProps) => {
  const { data: returnScrapSuppliers } = useGetReturnScrap(
    { active_only: true },
    true,
  );

  const { data: secondTierSuppliers } = useGetSupplier2nd(
    { active_only: true },
    true,
  );

  const { data: firstTierSuppliers } = useGetFirstTierSuppliers({
    skip: 0,
    limit: 2000,
  });

  const {
    materialData,
    rows,
    dimensionColumns,
    formulaColumns,
    otherColumns,
    totalColumns,
  } = useMemo(() => {
    const materialData = Array.isArray(materials)
      ? materials.find((matCode) => matCode.dimension_id === dimensionId)
      : undefined;
    const rowsData = materialData?.data || [];
    const dimensionColumns = materialData?.dimension_columns || [];
    const formulaColumns =
      materialData?.formula_columns?.sort((a, b) => {
        if (a.parameter === "others_cost") return 1;
        if (b.parameter === "others_cost") return -1;
        return 0;
      }) || [];
    const otherColumns = materialData?.others_cost_column || [];

    const lossParamsCount = formulaColumns.filter(
      (col) => col.parameter === "loss1" || col.parameter === "loss2",
    ).length;
    const regularFormulaParamsCount = formulaColumns.length - lossParamsCount;
    const totalFormulaColumns = regularFormulaParamsCount + lossParamsCount * 2;
    const totalColumns =
      9 + dimensionColumns.length + totalFormulaColumns + otherColumns.length;

    return {
      materialData,
      rows: rowsData,
      dimensionColumns: dimensionColumns,
      formulaColumns: formulaColumns,
      totalColumns: totalColumns,
      otherColumns: otherColumns,
    };
  }, [materials, dimensionId]);

  const materialTypes = useMemo(() => {
    const types = materialSupplierDetail?.material_type_ids || [];
    return types;
  }, [materialSupplierDetail]);


  const handleSelectRow = (row: MaterialCode, check: boolean) => {
    if (check) {
      const materialPayload: CreateMaterialCodePayload = {
        material_code: row.material_code || "",
        material_supplier_id: row.material_supplier_id || 0,
        material_type_id: row.material_type_id || 0,
        material_spec_id: row.material_spec_id || 0,
        material_dimension_id: dimensionId,
        first_tier_supplier_id: row.first_tier_supplier_id || 0,
        second_tier_supplier_id: row.second_tier_supplier_id || 0,
        is_active: tabActive === 0 ? true : false,
        dimension_parameters: dimensionColumns.reduce(
          (acc, col) => {
            acc[col.parameter] = Number(
              (row as Record<string, unknown>)?.[col.parameter] || "0",
            );
            return acc;
          },
          {} as Record<string, number>,
        ),
        formula_parameters: formulaColumns.reduce(
          (acc, col) => {
            acc[col.parameter] = Number(
              (row as Record<string, unknown>)?.[col.parameter] || "0",
            );
            if (col.parameter === "loss1" || col.parameter === "loss2") {
              const isPctKey = `is_${col.parameter}_pct`;
              acc[isPctKey] =
                (row as Record<string, unknown>)?.[isPctKey] === 1;
            }
            return acc;
          },
          {} as Record<string, number | boolean>,
        ),
        formula_id: 0,
        return_scrap_supplier_id:
          returnScrapSuppliers?.find(
            (supplier) => supplier.name === row.return_scrap_supplier_name,
          )?.id || 0,
        ref_part_no: row.ref_part_no || "",
        material_price: row.material_price || 0,
        others_cost_detail: row.others_cost_detail || [],
        material_id: row.material_id,
      };
      setSelectedMaterial([...selectedMaterial, materialPayload]);
    } else {
      setSelectedMaterial(
        selectedMaterial.filter((item) => item.material_id !== row.material_id),
      );
    }
  };

  const handleDataChange = (
    materialId: number,
    updates: Partial<CreateMaterialCodePayload>,
  ) => {
    if (!setSelectedMaterial || !selectedMaterial) return;
    setSelectedMaterial(
      selectedMaterial.map((item) =>
        item.material_id === materialId ? { ...item, ...updates } : item,
      ),
    );
  };

  const isRowSelected = (rowId: number) => {
    return (
      selectedMaterial?.some((item) => item.material_id === rowId) || false
    );
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (!setSelectedMaterial) return;

    if (isSelected) {
      const allMaterials = rows.map((row) => ({
        material_code: row.material_code || "",
        material_supplier_id: row.material_supplier_id || 0,
        material_type_id: row.material_type_id || 0,
        material_spec_id: row.material_spec_id || 0,
        material_dimension_id: dimensionId,
        first_tier_supplier_id: row.first_tier_supplier_id || 0,
        second_tier_supplier_id: row.second_tier_supplier_id || 0,
        is_active: tabActive === 0 ? true : false,
        dimension_parameters: dimensionColumns.reduce(
          (acc, col) => {
            acc[col.parameter] = Number(
              (row as Record<string, unknown>)?.[col.parameter] || "0",
            );
            return acc;
          },
          {} as Record<string, number>,
        ),
        formula_parameters: formulaColumns.reduce(
          (acc, col) => {
            acc[col.parameter] = Number(
              (row as Record<string, unknown>)?.[col.parameter] || "0",
            );
            if (col.parameter === "loss1" || col.parameter === "loss2") {
              const isPctKey = `is_${col.parameter}_pct`;
              acc[isPctKey] =
                (row as Record<string, unknown>)?.[isPctKey] === 1;
            }
            return acc;
          },
          {} as Record<string, number | boolean>,
        ),
        formula_id: 0,
        return_scrap_supplier_id:
          returnScrapSuppliers?.find(
            (supplier) => supplier.name === row.return_scrap_supplier_name,
          )?.id || 0,
        ref_part_no: row.ref_part_no || "",
        material_price: row.material_price || 0,
        others_cost_detail: [],
        material_id: row.material_id,
      }));
      setSelectedMaterial(allMaterials);
    } else {
      setSelectedMaterial([]);
    }
  };

  const isAllSelected =
    rows.length > 0 &&
    rows.every((row) => isRowSelected(row.material_id));


  return (
    <Box
      sx={{
        width: "100%",
        mt: 1,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "70vh",
          overflow: "auto",
          position: "relative",
          width: "100%",
          backgroundColor: "#f3f3f3ff",
        }}
      >
        <Table stickyHeader sx={{ tableLayout: "fixed", width: "auto" }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 0,
                  zIndex: 102,
                  width: 70,
                  minWidth: 70,
                  maxWidth: 70,
                  textAlign: "center",
                  borderRight: "1px solid #e0e0e0",
                }}
              >
                <Checkbox
                  checked={isAllSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  disabled={loading || isEditing || isAdding}
                  color="warning"
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor: "white",
                    },
                  }}
                />
              </TableCell>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 70,
                  zIndex: 102,
                  width: 500,
                  minWidth: 500,
                  maxWidth: 500,
                  borderRight: "2px solid #e0e0e0",
                }}
              >
                Material Code
              </TableCell>
              {isOpenDetail && (
                <>
                  <TableCell
                    sx={{
                      minWidth: 150,
                      maxWidth: 150,
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                    }}
                  >
                    Material Type
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 150,
                      maxWidth: 150,
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                    }}
                  >
                    Material Spec
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 150,
                      maxWidth: 150,
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                    }}
                  >
                    2nd Tier Supplier
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 150,
                      maxWidth: 150,
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                    }}
                  >
                    Return Scrap Supplier
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 150,
                      maxWidth: 150,
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                    }}
                  >
                    1st Tier Supplier
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 350,
                      maxWidth: 350,
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                    }}
                  >
                    Ref Part No.
                  </TableCell>
                </>
              )}
              {isOpenDimension &&
                dimensionColumns.map((col) => (
                  <TableCell
                    key={col.parameter}
                    sx={{
                      minWidth: 150,
                      maxWidth: 150,
                      textAlign: "center",
                      borderRight: "1px solid #e0e0e0",
                    }}
                  >
                    {col.name}
                  </TableCell>
                ))}
              <TableCell
                sx={{
                  width: 150,
                  minWidth: 150,
                  maxWidth: 150,
                  borderRight: "1px solid #e0e0e0",
                  borderLeft: "1px solid #e0e0e0",
                  textAlign: "center",
                  backgroundColor: "secondary.light",
                }}
              >
                Material Price{"\n"}(THB/kg)
              </TableCell>
              <TableCell
                sx={{
                  width: 150,
                  minWidth: 150,
                  maxWidth: 150,
                  borderRight: "1px solid #e0e0e0",
                  borderLeft: "1px solid #e0e0e0",
                  backgroundColor: "secondary.light",
                  textAlign: "center",
                }}
              >
                Base Price{"\n"}(THB/kg)
              </TableCell>
              {isOpenComponentPrice &&
                formulaColumns.map((col) => {
                  const isLossParam =
                    col.parameter == "loss1" || col.parameter == "loss2";

                  if (isLossParam) {
                    return (
                      <TableCell
                        key={`${col.parameter}`}
                        sx={{
                          minWidth: 240,
                          maxWidth: 240,
                          textAlign: "center",
                          borderRight: "1px solid #e0e0e0",
                          borderLeft: "1px solid #e0e0e0",
                        }}
                        colSpan={2}
                      >
                        {col.name}
                      </TableCell>
                    );
                  }

                  const isOtherCost =
                    col.parameter.toLocaleLowerCase() === "others_cost";

                  if (isOtherCost) {
                    return (
                      <TableCell
                        key={`${col.parameter}`}
                        sx={{
                          minWidth: 150,
                          maxWidth: 150,
                          textAlign: "center",
                          borderRight: "1px solid #e0e0e0",
                          borderLeft: "1px solid #e0e0e0",
                          backgroundColor: "crimson",
                        }}
                      >
                        {col.name}
                      </TableCell>
                    );
                  }

                  return (
                    <TableCell
                      key={col.parameter}
                      sx={{
                        minWidth: 150,
                        maxWidth: 150,
                        textAlign: "center",
                        borderRight: "1px solid #e0e0e0",
                        borderLeft: "1px solid #e0e0e0",
                      }}
                    >
                      {col.name}
                    </TableCell>
                  );
                })}
              {isOpenOtherCost &&
                otherColumns.map((col) => (
                  <TableCell
                    key={col.id}
                    sx={{
                      minWidth: 150,
                      maxWidth: 150,
                      textAlign: "center",
                      borderRight: "1px solid #e0e0e0",
                    }}
                  >
                    {col.name}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={totalColumns}>
                  <Skeleton variant="text" height={40} animation="wave" />
                </TableCell>
              </TableRow>
            ) : (
              <>
                {isAdding && (
                  <EditRow
                    isAdding={isAdding}
                    isEditing={false}
                    material={newMaterial}
                    setMaterial={setNewMaterial}
                    dimensionName={dimensionName}
                    materialTypes={materialTypes}
                    materialSupplierDetail={materialSupplierDetail}
                    secondTierSuppliers={secondTierSuppliers}
                    returnScrapSuppliers={returnScrapSuppliers}
                    firstTierSuppliers={firstTierSuppliers}
                    dimensionColumns={dimensionColumns}
                    formulaColumns={formulaColumns}
                    otherColumns={otherColumns}
                    materialData={materialData}
                    isOpenDetail={isOpenDetail}
                    isOpenDimension={isOpenDimension}
                    isOpenComponentPrice={isOpenComponentPrice}
                    isOpenOtherCost={isOpenOtherCost}
                    validationErrors={validationErrors.get(0) || new Set()}
                    refPartNo={newMaterial.ref_part_no || ""}
                  />
                )}

                {rows.map((row) => {
                  const isRowSelectedForEdit =
                    isEditing && isRowSelected(row.material_id);
                  const selectedMaterialItem = selectedMaterial.find(
                    (item) => item.material_id === row.material_id,
                  );

                  if (isRowSelectedForEdit && selectedMaterialItem) {
                    return (
                      <EditRow
                        key={row.material_id}
                        isAdding={false}
                        isEditing={true}
                        material={selectedMaterialItem}
                        setMaterial={(updatedMaterial) => {
                          if (updatedMaterial) {
                            handleDataChange(row.material_id, updatedMaterial);
                          }
                        }}
                        dimensionName={dimensionName}
                        materialTypes={materialTypes}
                        materialSupplierDetail={materialSupplierDetail}
                        secondTierSuppliers={secondTierSuppliers}
                        returnScrapSuppliers={returnScrapSuppliers}
                        firstTierSuppliers={firstTierSuppliers}
                        dimensionColumns={dimensionColumns}
                        formulaColumns={formulaColumns}
                        otherColumns={otherColumns}
                        materialData={materialData}
                        isOpenDetail={isOpenDetail}
                        isOpenDimension={isOpenDimension}
                        isOpenComponentPrice={isOpenComponentPrice}
                        isOpenOtherCost={isOpenOtherCost}
                        validationErrors={
                          validationErrors.get(row.material_id) || new Set()
                        }
                        refPartNo={row.ref_part_no || ""}
                      />
                    );
                  }

                  return (
                    <ViewRow
                      key={row.material_id}
                      row={row}
                      isSelected={isRowSelected(row.material_id)}
                      onSelectChange={handleSelectRow}
                      dimensionColumns={dimensionColumns}
                      formulaColumns={formulaColumns}
                      otherColumns={otherColumns}
                      materialSupplierDetail={materialSupplierDetail}
                      isEditing={isEditing}
                      isAdding={isAdding}
                      materialData={materialData}
                      isOpenDetail={isOpenDetail}
                      isOpenDimension={isOpenDimension}
                      isOpenComponentPrice={isOpenComponentPrice}
                      isOpenOtherCost={isOpenOtherCost}
                    />
                  );
                })}
              </>
            )}
            {!loading && !isAdding && rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={totalColumns}
                  align="center"
                  sx={{ height: 150, verticalAlign: "middle", fontWeight: 500 }}
                >
                  ไม่มีข้อมูล
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Box>
  );
};

export default MaterialCodeTable;
